import React, {useState} from 'react';

import {HeaderFilter} from 'src/components/header-filter';
import {FILTER_WIDTH} from 'src/constants';

import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import Label from '@pb/kit-component-label';
import {Cell, Head, Row} from '@pb/kit-component-table';

import {emailW, firstNameW, lastNameW, numberW, roleW, selectW, toolW, vendorW} from '../constants';

import type {Role} from 'src/openapi/admin/models/Role';
import type {Tool} from 'src/openapi/admin/models/Tool';
import type {Vendor} from 'src/openapi/admin/models/Vendor';


type Props = {
    totalCount: number,
    allSelected: boolean,
    onSelectAll: () => void,
    onClearAll: () => void,
    emailSortDir: 'ASC' | 'DESC',
    onEmailSortChange: (d: 'ASC' | 'DESC') => void,
    roleId?: string,
    onRoleIdChange: (id?: string) => void,
    vendorId?: string,
    onVendorIdChange: (id?: string) => void,
    toolId?: string,
    onToolIdChange: (id?: string) => void,
    roles: Role[],
    vendors: Vendor[],
    tools: Tool[],
};

export const AccessManagementTableHeader: React.FC<Props> = ({
    totalCount,
    allSelected,
    onSelectAll,
    onClearAll,
    emailSortDir,
    onEmailSortChange,
    roleId,
    onRoleIdChange,
    vendorId,
    onVendorIdChange,
    toolId,
    onToolIdChange,
    roles,
    vendors,
    tools,
}) => {
    const [tempRoleId, setTempRoleId] = useState<string | undefined>(roleId);
    const [tempVendorId, setTempVendorId] = useState<string | undefined>(vendorId);
    const [tempToolId, setTempToolId] = useState<string | undefined>(toolId);
    const toggleEmailSort = () => onEmailSortChange(emailSortDir === 'ASC' ? 'DESC' : 'ASC');
    return (
        <Head>
            <Row>
                <Cell width={numberW}>#</Cell>
                <Cell className="text-align_center" width={selectW}>
                    <CheckBox
                        checked={allSelected ? totalCount > 0 : undefined}
                        onCheckBoxChange={({event}: {event: React.ChangeEvent<HTMLInputElement>}) =>
                            event.target.checked ? onSelectAll() : onClearAll()}
                        disabled={totalCount === 0}
                        usePropsOnly={true}
                    />
                </Cell>
                <Cell width={emailW}>
                    <HeaderFilter
                        caption="Email"
                        filterActive={undefined}
                        sortable={true}
                        showFilter={false}
                        direction={emailSortDir === 'ASC' ? 'ASC' : 'DESC'}
                        defaultDirection="ASC"
                        onSort={toggleEmailSort}
                        onReset={() => {}}
                        onApply={() => {}}
                        onCancel={() => {}}>
                        <div/>
                    </HeaderFilter>
                </Cell>
                <Cell width={firstNameW}>First Name</Cell>
                <Cell width={lastNameW}>Last Name</Cell>
                <Cell width={vendorW} verticalAlign="top">
                    <HeaderFilter
                        caption="Vendor Name"
                        filterActive={vendorId ? true : undefined}
                        sortable={false}
                        direction={undefined}
                        defaultDirection="ASC"
                        onSort={() => {}}
                        onReset={() => {
                            setTempVendorId(undefined);
                            onVendorIdChange(undefined);
                        }}
                        onApply={() => onVendorIdChange(tempVendorId)}
                        onCancel={() => setTempVendorId(vendorId)}
                        contentWidth={FILTER_WIDTH}>
                        <div>
                            <Label>Vendor Name</Label>
                            <ComboBox<string, string>
                                value={tempVendorId}
                                onChange={val => setTempVendorId(Array.isArray(val) ? val[0] : val)}
                                placeholder="Select vendor">
                                {vendors.map(v => (
                                    <ComboboxItem key={v.id} caption={v.vendorName} value={v.id}/>
                                ))}
                            </ComboBox>
                        </div>
                    </HeaderFilter>
                </Cell>
                <Cell width={roleW} verticalAlign="top">
                    <HeaderFilter
                        caption="Role"
                        filterActive={roleId ? true : undefined}
                        sortable={false}
                        direction={undefined}
                        defaultDirection="ASC"
                        onSort={() => {}}
                        onReset={() => {
                            setTempRoleId(undefined);
                            onRoleIdChange(undefined);
                        }}
                        onApply={() => onRoleIdChange(tempRoleId)}
                        onCancel={() => setTempRoleId(roleId)}
                        contentWidth={FILTER_WIDTH}>
                        <div>
                            <Label>Role</Label>
                            <ComboBox<string, string>
                                value={tempRoleId}
                                onChange={val => setTempRoleId(Array.isArray(val) ? val[0] : val)}
                                placeholder="Select role">
                                {roles.map(r => (
                                    <ComboboxItem key={r.id} caption={r.name} value={r.id}/>
                                ))}
                            </ComboBox>
                        </div>
                    </HeaderFilter>
                </Cell>
                <Cell width={toolW} verticalAlign="top">
                    <HeaderFilter
                        caption="Tools"
                        filterActive={toolId ? true : undefined}
                        sortable={false}
                        direction={undefined}
                        defaultDirection="ASC"
                        onSort={() => {}}
                        onReset={() => {
                            setTempToolId(undefined);
                            onToolIdChange(undefined);
                        }}
                        onApply={() => onToolIdChange(tempToolId)}
                        onCancel={() => setTempToolId(toolId)}
                        contentWidth={FILTER_WIDTH}>
                        <div>
                            <Label>Tools</Label>
                            <ComboBox<string, string>
                                value={tempToolId}
                                onChange={val => setTempToolId(Array.isArray(val) ? val[0] : val)}
                                placeholder="Select tool">
                                {tools.map(t => (
                                    <ComboboxItem key={t.id} caption={t.name} value={t.id}/>
                                ))}
                            </ComboBox>
                        </div>
                    </HeaderFilter>
                </Cell>
            </Row>
        </Head>
    );
};
